
import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ref, get } from 'firebase/database';
import { realtimeDb } from '../../firebase';
import { FaArrowLeft, FaPrint, FaWhatsapp, FaFilePdf } from 'react-icons/fa';
import { normalizeCompanyKey, firebaseCompanyKey } from '../../Utils/companyUtils';
import { formatDate } from '../../Utils/dateUtils';

// Use global variables from CDN
const jsPDF = window.jspdf.jsPDF;
const html2canvas = window.html2canvas;

const EntryPassPrintView = () => {
  const { company: companyParam, division, ids } = useParams();
  const [entries, setEntries] = useState([]);
  const [address, setAddress] = useState('');
  const [logo, setLogo] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const printRef = useRef();

  useEffect(() => {
    const fetchData = async () => {
      if (!ids || !companyParam || !division) {
        setError('Missing required parameters');
        setLoading(false);
        return;
      }

      try {
        const normalizedCompany = normalizeCompanyKey(companyParam);
        const sanitizedCompany = firebaseCompanyKey(normalizedCompany);
        const sanitizedDivision = division;
        
        const idList = ids.split(',');
        const list = [];

        const basePath = `companies/${sanitizedCompany}/${sanitizedDivision}`;
        const entryPath = `${basePath}/entryPass`;
        
        for (let id of idList) {
          const snap = await get(ref(realtimeDb, `${entryPath}/${id}`));
          if (snap.exists()) {
            list.push({ ...snap.val(), id });
          } else {
            console.warn(`Entry ${id} not found at ${entryPath}`);
          }
        }

        if (list.length === 0) {
          console.log('Trying fallback path structure...');
          const fallbackPath = `${sanitizedCompany}/${sanitizedDivision}/entryPass`;
          for (let id of idList) {
            const snap = await get(ref(realtimeDb, `${fallbackPath}/${id}`));
            if (snap.exists()) {
              list.push({ ...snap.val(), id });
            }
          }
        }

        if (list.length === 0) {
          setError('No entry data found in any path structure');
          setLoading(false);
          return;
        }

        const addrRef = ref(realtimeDb, `companyDetails/${sanitizedCompany}`);
        const addrSnap = await get(addrRef);
        if (addrSnap.exists()) {
          const data = addrSnap.val();
          setAddress(data.address || 'Address not available');
          setLogo(data.logo || '');
        } else {
          setAddress('Address not available');
          setLogo('');
        }

        setEntries(list);
        setError(null);
      } catch (err) {
        console.error('Error fetching data:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [companyParam, division, ids]);

  const handlePrint = () => {
    setTimeout(() => {
      window.print();
    }, 500);
  };

  const handleWhatsAppShare = () => {
    if (!entries.length) {
      alert('No data available to share.');
      return;
    }

    const { challanNo, date, vehicleNo, finalChallanNo, totalOil, oilChallanNo } = entries[0];
    const normalizedCompany = normalizeCompanyKey(companyParam);
    const totalOilCap = entries.reduce((sum, row) => sum + parseFloat(row.oilCap || 0), 0).toFixed(2);
    const totalKg = entries.reduce((sum, row) => sum + parseFloat(row.kg || 0), 0).toFixed(2);

    const tableData = entries.map((row, idx) => {
      return `${idx + 1}. Job No: ${row.jobNo}, KVA: ${row.kva}, Make: ${row.make}, Make SR No: ${row.makeSrNo}, Type: ${row.type}, Oil Cap: ${row.oilCap}, KG: ${row.kg || 0}`;
    }).join('\n');

    const message = `
Entry Pass - ${normalizedCompany} (${division} Division)
Address: ${address}
Challan No: ${challanNo}
Date: ${formatDate(date)}
Vehicle No: ${vehicleNo}
Final Challan No: ${finalChallanNo || 'N/A'}
Total Oil: ${totalOil} L
Total Oil Cap: ${totalOilCap} L
Total KG: ${totalKg} kg
Oil Challan No: ${oilChallanNo || 'N/A'}

Entries:
${tableData}
`.trim();

    const encodedMessage = encodeURIComponent(message);
    const whatsappUrl = `https://wa.me/?text=${encodedMessage}`;
    window.open(whatsappUrl, '_blank');
  };

  const handleWhatsAppSharePDF = async () => {
    if (!entries.length || !printRef.current) {
      alert('No data available to share as PDF.');
      return;
    }

    try {
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });

      const elements = printRef.current.querySelectorAll('.print-area');
      for (let i = 0; i < elements.length; i++) {
        const canvas = await html2canvas(elements[i], {
          scale: 2,
          useCORS: true,
          logging: false,
          allowTaint: true,
          width: elements[i].offsetWidth,
          height: elements[i].offsetHeight
        });
        const imgData = canvas.toDataURL('image/png');
        const imgProps = pdf.getImageProperties(imgData);
        const pdfWidth = pdf.internal.pageSize.getWidth() - 20;
        const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;

        if (i > 0) pdf.addPage();
        pdf.addImage(imgData, 'PNG', 10, 10, pdfWidth, pdfHeight);
      }

      const pdfBlob = pdf.output('blob');
      const pdfUrl = URL.createObjectURL(pdfBlob);
      const normalizedCompany = normalizeCompanyKey(companyParam);
      const link = document.createElement('a');
      link.href = pdfUrl;
      link.download = `EntryPass_${normalizedCompany}_${division}_${entries[0].challanNo}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      setTimeout(() => {
        const message = `Entry Pass - ${normalizedCompany} (${division} Division) PDF`;
        const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(message)}`;
        window.open(whatsappUrl, '_blank');
      }, 1000);

      URL.revokeObjectURL(pdfUrl);
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Failed to generate PDF. Please try again.');
    }
  };

  if (loading) return <div className="p-6 text-lg font-bold">Loading...</div>;
  if (error) return <div className="p-6 text-lg font-bold text-red-600">Error: {error}</div>;
  if (!entries.length) return <div className="p-6 text-lg font-bold">No entry data to display</div>;

  const { challanNo, date, vehicleNo, finalChallanNo, totalOil, oilChallanNo } = entries[0];
  const normalizedCompany = normalizeCompanyKey(companyParam);
  const totalOilCap = entries.reduce((sum, row) => sum + parseFloat(row.oilCap || 0), 0).toFixed(2);
  const totalKg = entries.reduce((sum, row) => sum + parseFloat(row.kg || 0), 0).toFixed(2);

  const renderEntryPass = (copyTitle) => (
    <div
      className="print-area bg-white"
      style={{
        width: '210mm',
        minHeight: '297mm',
        boxSizing: 'border-box',
        border: '2px solid black',
        pageBreakAfter: 'always',
        margin: '0 auto',
        fontFamily: 'Arial, sans-serif',
        padding: '15mm',
        fontSize: '14pt',
        lineHeight: 1.5
      }}
    >
      <div className="flex items-center justify-start gap-4 mb-8">
        {logo && (
          <img
            src={logo}
            alt={`${normalizedCompany} Logo`}
            style={{ width: '40mm', height: '40mm', objectFit: 'contain' }}
          />
        )}
        <div className="text-left company-details">
          <h1 className="text-3xl font-bold uppercase mb-2" style={{ fontSize: '18pt' }}>{normalizedCompany}</h1>
          <div className="text-xl font-bold mb-1" style={{ fontSize: '16pt' }}>Transformer Service</div>
          <div className="text-xl font-bold mb-2" style={{ fontSize: '16pt' }}>{division} Entry Pass</div>
          <div className="text-lg font-semibold" style={{ fontSize: '12pt' }}>{address}</div>
        </div>
      </div>
      <hr className="border-t-2 border-black my-4" />
      <div className="text-center mb-6 font-bold underline gate-pass-title" style={{ fontSize: '18pt' }}>
        ENTRY PASS
      </div>

      <div className="grid grid-cols-2 gap-4 mb-8 info-grid" style={{ fontSize: '14pt' }}>
        <div><strong>Date:</strong> {formatDate(date)}</div>
        <div><strong>Vehicle No:</strong> {vehicleNo}</div>
        <div><strong>Challan No:</strong> {challanNo}</div>
        <div><strong>Total Oil:</strong> {totalOil} L</div>
        <div><strong>Total Oil Cap:</strong> {totalOilCap} L</div>
        <div><strong>Oil Challan No:</strong> {oilChallanNo || 'N/A'}</div>
        <div><strong>Total KG:</strong> {totalKg} kg</div>
      </div>

      <table className="w-full mb-12" style={{ fontSize: '14pt' }}>
        <thead>
          <tr className="bg-gray-200">
            <th className="border border-black p-1">SR No</th>
            <th className="border border-black p-1">Job No</th>
            <th className="border border-black p-1">KVA</th>
            <th className="border border-black p-1">Make</th>
            <th className="border border-black p-1">Make SR No</th>
            <th className="border border-black p-1">Type</th>
            <th className="border border-black p-1">Oil Cap</th>
            <th className="border border-black p-1">KG</th>
          </tr>
        </thead>
        <tbody>
          {entries.map((row, idx) => (
            <tr key={idx} className="border" style={{ lineHeight: 1.8 }}>
              <td className="border border-black p-1 text-center">{idx + 1}</td>
              <td className="border border-black p-1 text-center">{row.jobNo}</td>
              <td className="border border-black p-1 text-center">{row.kva}</td>
              <td className="border border-black p-1 text-center">{row.make}</td>
              <td className="border border-black p-1 text-center">{row.makeSrNo}</td>
              <td className="border border-black p-1 text-center">{row.type}</td>
              <td className="border border-black p-1 text-center">{row.oilCap}</td>
              <td className="border border-black p-1 text-center">{row.kg || '0.00'}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="flex justify-between mt-8 signature-area" style={{ fontSize: '14pt' }}>
        <div className="text-center">
          <div className="border-t-2 border-black w-48 inline-block mt-8"></div>
          <div>Sign of Receiver</div>
        </div>
        <div className="text-center">
          <div className="border-t-2 border-black w-48 inline-block mt-8"></div>
          <div>Sign of {normalizedCompany}</div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="p-4 bg-gray-100 min-h-screen">
      <div className="sticky top-0 z-50 bg-white shadow-md border-b px-4 py-3 flex items-center justify-between no-print">
        <FaArrowLeft className="cursor-pointer text-gray-700" onClick={() => navigate(-1)} />
        <h2 className="text-xl font-bold">{normalizedCompany} / {division} / Entry Pass</h2>
        <div className="flex items-center gap-2">
          <button
            onClick={handleWhatsAppShare}
            disabled={loading || !entries.length}
            className={`flex items-center gap-2 px-4 py-2 rounded ${
              loading || !entries.length
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-green-600 text-white hover:bg-green-700'
            }`}
          >
            <FaWhatsapp className="text-xl" />
            Share Text
          </button>
          <button
            onClick={handleWhatsAppSharePDF}
            disabled={loading || !entries.length}
            className={`flex items-center gap-2 px-4 py-2 rounded ${
              loading || !entries.length
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-red-600 text-white hover:bg-red-700'
            }`}
          >
            <FaFilePdf className="text-xl" />
            Share PDF
          </button>
          <button
            onClick={handlePrint}
            disabled={loading || !entries.length}
            className={`flex items-center gap-2 px-4 py-2 rounded ${
              loading || !entries.length
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-blue-600 text-white hover:bg-blue-700'
            }`}
          >
            <FaPrint className="text-xl" />
            Print
          </button>
        </div>
      </div>

      <div ref={printRef} className="print-area-wrapper">
        {renderEntryPass('Copy 1')}
        {renderEntryPass('Copy 2')}
      </div>

      <style>
        {`
          @media print {
            body {
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
              margin: 0;
              padding: 0;
              font-family: 'Arial', sans-serif;
              font-size: 14pt;
            }
            .no-print {
              display: none !important;
            }
            .print-area-wrapper {
              margin: 0;
              padding: 0;
            }
            .print-area {
              display: block !important;
              page-break-after: always;
              page-break-inside: avoid;
            }
            table, th, td {
              border: 1.5px solid black !important;
            }
            th {
              font-weight: bold !important;
            }
            td {
              font-weight: 600 !important;
            }
            img {
              max-width: 40mm;
              max-height: 40mm;
              object-fit: contain;
            }
            hr {
              border-top: 2px solid black;
              margin: 16px 0;
            }
            .company-details {
              line-height: 1.3;
              padding-left: 8px;
            }
            .gate-pass-title {
              font-size: 18pt;
              margin: 15px 0;
              text-decoration: underline;
            }
            .info-grid {
              font-size: 14pt;
              margin-bottom: 20px;
            }
            .signature-area {
              font-size: 14pt;
              margin-top: 32px;
            }
          }
          .company-details {
            line-height: 1.3;
            padding-left: 8px;
          }
          hr {
            border-top: 2px solid black;
            margin: 16px 0;
          }
        `}
      </style>
    </div>
  );
};

export default EntryPassPrintView;
